import { useEffect, useMemo, useState } from 'react'
import { Bell, Loader2, Send } from 'lucide-react'
import {
  fireTestNotification,
  listAdminNotificationTypes,
  type AdminNotificationType,
} from '../../lib/api'
import { StyledSelect, type StyledSelectOption } from '../../components/ui/StyledSelect'

const PRIORITY_BADGE: Record<string, string> = {
  high: 'bg-red-500/20 text-red-500',
  medium: 'bg-amber-500/20 text-amber-500',
  low: 'bg-slate-500/20 text-slate-400',
}

const PRIORITY_LABEL: Record<string, string> = {
  high: 'High priority',
  medium: 'Medium priority',
  low: 'Low priority',
}

export default function TestNotifications() {
  const [types, setTypes] = useState<AdminNotificationType[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [selectedType, setSelectedType] = useState<string>('')
  const [targetEmail, setTargetEmail] = useState('')
  const [appName, setAppName] = useState('')
  const [firing, setFiring] = useState(false)
  const [lastResult, setLastResult] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  const typeOptions: StyledSelectOption[] = useMemo(
    () =>
      types.map((t) => ({
        value: t.type,
        label: t.type,
        description: PRIORITY_LABEL[t.priority] ?? t.priority,
      })),
    [types],
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listAdminNotificationTypes()
      .then((res) => {
        if (cancelled) return
        setTypes(res.types)
        if (res.types.length > 0) setSelectedType(res.types[0].type)
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  async function onFire() {
    if (!selectedType || !targetEmail) return
    setFiring(true)
    setLastResult(null)
    try {
      const res = await fireTestNotification({
        notification_type: selectedType,
        target_user_email: targetEmail.trim(),
        app_name: appName.trim() || undefined,
      })
      setLastResult({
        kind: 'ok',
        text: `Fired ${res.notification_type} → ${res.target_user_email}`,
      })
    } catch (err) {
      setLastResult({
        kind: 'err',
        text: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setFiring(false)
    }
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl">
      <div className="flex items-center gap-3 mb-2">
        <Bell className="w-5 h-5 text-amber-500" />
        <h1 className="text-xl sm:text-2xl font-semibold">Test Notifications</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Fire any notification type at a target user with stub data. Useful for verifying
        in-app bell rendering, push delivery, and deep-link routing without waiting for
        a real platform event. The recipient&apos;s notification preferences are respected
        — disable a type in their Settings to verify suppression.
      </p>

      {loadError ? (
        <div className="p-4 rounded-lg bg-red-500/10 text-red-500 text-sm">
          Failed to load notification types: {loadError}
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading types…
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Notification type</label>
            <StyledSelect
              value={selectedType}
              onChange={setSelectedType}
              className="w-full justify-between"
              options={typeOptions}
            />
            {selectedType ? (
              <div className="mt-2">
                <span
                  className={`inline-block px-2 py-0.5 text-xs rounded ${
                    PRIORITY_BADGE[
                      types.find((t) => t.type === selectedType)?.priority ?? 'low'
                    ]
                  }`}
                >
                  priority: {types.find((t) => t.type === selectedType)?.priority}
                </span>
              </div>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Target user email</label>
            <input
              type="email"
              value={targetEmail}
              onChange={(e) => setTargetEmail(e.target.value)}
              placeholder="user@exante.eu"
              className="w-full p-2 rounded-lg border border-border bg-background"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              App name <span className="text-muted-foreground">(optional — defaults to &quot;test-app&quot;)</span>
            </label>
            <input
              type="text"
              value={appName}
              onChange={(e) => setAppName(e.target.value)}
              placeholder="my-app"
              className="w-full p-2 rounded-lg border border-border bg-background"
            />
          </div>

          <button
            onClick={onFire}
            disabled={firing || !selectedType || !targetEmail}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
          >
            {firing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {firing ? 'Firing…' : 'Fire notification'}
          </button>

          {lastResult ? (
            <div
              className={`p-3 rounded-lg text-sm ${
                lastResult.kind === 'ok'
                  ? 'bg-green-500/10 text-green-500'
                  : 'bg-red-500/10 text-red-500'
              }`}
            >
              {lastResult.text}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
